/**
 * Ring Size Selector JavaScript
 * Handles ring size selection functionality and form validation
 */

class RingSizeSelector {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateFormValidation();
  }

  bindEvents() {
    // Handle dropdown changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('ring-size-select')) {
        this.handleSizeChange(e.target);
      }
    });

    // Handle button selection
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('ring-size-button')) {
        this.handleButtonSelection(e.target);
      }
    });

    // Form submission validation
    document.addEventListener('submit', (e) => {
      const form = e.target;
      // Check for both data-product-form and data-type="add-to-cart-form" attributes
      if (form.matches('form[data-product-form], form[data-type="add-to-cart-form"], form[action*="/cart/add"]')) {
        this.validateFormSubmission(e);
      }
    });
  }

  handleSizeChange(selectElement) {
    const selectedValue = selectElement.value;
    const form = selectElement.closest('form');
    
    if (selectedValue) {
      this.updateSelectedSizeDisplay(selectElement, selectedValue);
      this.enableAddToCart(form);
      this.hideErrorMessage(form);
    } else {
      this.disableAddToCart(form);
    }
  }

  handleButtonSelection(buttonElement) {
    const radioInput = buttonElement.previousElementSibling;
    const form = buttonElement.closest('form');
    
    if (radioInput && radioInput.type === 'radio') {
      radioInput.checked = true;
      this.updateButtonSelection(buttonElement);
      this.enableAddToCart(form);
      this.hideErrorMessage(form);
    }
  }

  updateSelectedSizeDisplay(selectElement, value) {
    // Update any display elements that show the selected size
    const displayElements = selectElement.parentElement.querySelectorAll('[data-selected-size]');
    displayElements.forEach(element => {
      element.textContent = value;
      element.classList.remove('hidden');
    });
  }

  updateButtonSelection(selectedButton) {
    // Remove active state from all buttons
    const allButtons = selectedButton.parentElement.querySelectorAll('.ring-size-button');
    allButtons.forEach(button => {
      button.classList.remove('active');
    });
    
    // Add active state to selected button
    selectedButton.classList.add('active');
  }

  enableAddToCart(form) {
    const addToCartButton = form.querySelector('[type="submit"], .product-form__submit');
    if (addToCartButton) {
      addToCartButton.disabled = false;
      addToCartButton.classList.remove('disabled');
    }
  }

  disableAddToCart(form) {
    const addToCartButton = form.querySelector('[type="submit"], .product-form__submit');
    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.classList.add('disabled');
    }
  }

  validateFormSubmission(event) {
    const form = event.target;
    const ringSizeSelect = form.querySelector('select[name="properties[Ring Size]"]');
    const ringSizeRadio = form.querySelector('input[name="properties[Ring Size]"]:checked');
    
    // Check if ring size selector exists on this form
    const hasRingSizeSelector = ringSizeSelect || form.querySelector('input[name="properties[Ring Size]"]');
    
    if (hasRingSizeSelector) {
      let hasValidSelection = false;
      
      // Check dropdown selection
      if (ringSizeSelect && ringSizeSelect.value && ringSizeSelect.value !== '') {
        hasValidSelection = true;
      }
      
      // Check radio button selection
      if (ringSizeRadio && ringSizeRadio.value) {
        hasValidSelection = true;
      }
      
      if (!hasValidSelection) {
        event.preventDefault();
        this.showSizeRequiredMessage(form);
        return false;
      }
    }
    
    return true;
  }

  showSizeRequiredMessage(form) {
    // Find the ring size selector and its static error message
    const ringSizeSelector = form.querySelector('.ring-size-selector');
    const errorMessage = ringSizeSelector ? ringSizeSelector.querySelector('.ring-size-error') : null;
    
    if (errorMessage) {
      // Show the static error message
      errorMessage.style.display = 'block';
      
      // Scroll to the ring size selector
      ringSizeSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Add error styling to ring size inputs
      const ringSizeSelect = form.querySelector('select[name="properties[Ring Size]"]');
      const ringSizeButtons = form.querySelector('.ring-size-buttons');
      
      if (ringSizeSelect) {
        ringSizeSelect.style.borderColor = '#c25151';
        ringSizeSelect.style.boxShadow = '0 0 0 1px #c25151';
        ringSizeSelect.focus();
      }
      
      if (ringSizeButtons) {
        ringSizeButtons.style.border = '1px solid #c25151';
        ringSizeButtons.style.borderRadius = '4px';
        ringSizeButtons.style.padding = '0.5rem';
      }

      // Auto-hide after 5 seconds
      setTimeout(() => {
        errorMessage.style.display = 'none';
        
        // Reset styles
        if (ringSizeSelect) {
          ringSizeSelect.style.borderColor = '';
          ringSizeSelect.style.boxShadow = '';
        }
        if (ringSizeButtons) {
          ringSizeButtons.style.border = '';
          ringSizeButtons.style.borderRadius = '';
          ringSizeButtons.style.padding = '';
        }
      }, 5000);
    }
  }

  hideErrorMessage(form) {
    // Find and hide the error message
    const ringSizeSelector = form.querySelector('.ring-size-selector');
    const errorMessage = ringSizeSelector ? ringSizeSelector.querySelector('.ring-size-error') : null;
    
    if (errorMessage) {
      errorMessage.style.display = 'none';
      
      // Reset input styles
      const ringSizeSelect = form.querySelector('select[name="properties[Ring Size]"]');
      const ringSizeButtons = form.querySelector('.ring-size-buttons');
      
      if (ringSizeSelect) {
        ringSizeSelect.style.borderColor = '';
        ringSizeSelect.style.boxShadow = '';
      }
      
      if (ringSizeButtons) {
        ringSizeButtons.style.border = '';
        ringSizeButtons.style.borderRadius = '';
        ringSizeButtons.style.padding = '';
      }
    }
  }

  updateFormValidation() {
    // Check if any ring size is already selected on page load
    const forms = document.querySelectorAll('form[data-product-form], form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    forms.forEach(form => {
      const hasRingSizeSelector = form.querySelector('select[name="properties[Ring Size]"], input[name="properties[Ring Size]"]');
      
      // Only apply validation if the form has a ring size selector
      if (hasRingSizeSelector) {
        const ringSizeInput = form.querySelector('select[name="properties[Ring Size]"], input[name="properties[Ring Size]"]:checked');
        
        // Don't disable the button initially - let the user try to submit and show error if needed
        // This provides better UX as it doesn't confuse users why the button is disabled
        if (!ringSizeInput || !ringSizeInput.value) {
          // Just mark the form as requiring ring size validation
          form.dataset.requiresRingSize = 'true';
        }
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new RingSizeSelector();
});

// Initialize for dynamic content (e.g., AJAX loaded products)
document.addEventListener('shopify:section:load', () => {
  new RingSizeSelector();
});

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RingSizeSelector;
}
